import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminContext = createContext(null);

export const useAdmin = () => {
    const context = useContext(AdminContext);
    if (!context) {
        throw new Error('useAdmin must be used within AdminProvider');
    }
    return context;
};

export const AdminProvider = ({ children }) => {
    const [admin, setAdmin] = useState(null);
    const [loading, setLoading] = useState(true);

    const axiosInstance = axios.create({
        baseURL: API,
        withCredentials: true
    });

    const fetchAdmin = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/admin/me`, { withCredentials: true });
            setAdmin(response.data);
        } catch (error) {
            setAdmin(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAdmin();
    }, [fetchAdmin]);

    const login = async (email, password, rememberMe = false) => {
        const response = await axios.post(`${API}/admin/login`, { email, password, remember_me: rememberMe }, {
            withCredentials: true
        });
        const { admin: adminData } = response.data;
        setAdmin(adminData);
        return adminData;
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
        } catch (error) {
            console.error('Logout error:', error);
        }
        setAdmin(null);
    };

    const value = {
        admin,
        loading,
        login,
        logout,
        isAuthenticated: !!admin,
        isSuperAdmin: admin?.role === 'super_admin',
        api: axiosInstance
    };

    return (
        <AdminContext.Provider value={value}>
            {children}
        </AdminContext.Provider>
    );
};
